/**
 * iter354 — offline write queue.
 *
 * Every screen already writes to localStorage first, so the app READS fine
 * with no signal. What was lost was the server half of each write: a logged
 * workout, a meal, a walking session or a goal change made in a basement gym
 * simply never reached Mongo, and the user only found out when the next
 * device showed stale data.
 *
 * Mutating requests that fail for NETWORK reasons are parked in IndexedDB and
 * replayed in order once the connection is back. 4xx responses are NOT queued
 * — a rejected payload will be rejected again forever.
 */
import { idbLoad, idbSave } from './persistentStorage';
import { getToken } from './tokenStorage';

export const OFFLINE_QUEUE_EVENT = 'fitness:offline-queue-changed';

const QUEUE_KEY = 'offline_write_queue';
const MAX_ENTRIES = 300;
const MAX_BODY_BYTES = 512 * 1024;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Endpoints that must never be queued: they rebuild themselves from the local
 *  snapshot on the next run (backup/sync) or are pointless to replay later
 *  (AI calls, analytics beacons, auth). */
const SKIP = [/\/api\/sync\//, /\/api\/backup\//, /\/api\/ai\//, /\/api\/analytics/, /\/api\/auth\//];

export interface QueuedWrite {
  id: string;
  url: string;
  method: string;
  body?: string;
  label: string;
  ts: number;
  attempts: number;
  /** Account the write belongs to — a queued workout must never replay into
   *  somebody else's account on a shared device (iter357). */
  userId?: string;
  /** Collapses repeated writes of the same resource to the newest one. */
  dedupeKey?: string;
}

/** user_id claim from the current JWT, used to scope queued writes. */
export function currentUserId(): string | null {
  const token = getToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload?.user_id || payload?.sub || null;
  } catch { return null; }
}

let queue: QueuedWrite[] | null = null;
let flushing = false;

async function load(): Promise<QueuedWrite[]> {
  if (queue) return queue;
  queue = (await idbLoad<QueuedWrite[]>(QUEUE_KEY)) || [];
  return queue;
}

function notify(): void {
  try {
    window.dispatchEvent(new CustomEvent(OFFLINE_QUEUE_EVENT, {
      detail: { pending: pendingWritesSync() },
    }));
  } catch { /* noop */ }
}

async function persist(): Promise<void> {
  await idbSave(QUEUE_KEY, queue || []);
  notify();
}

/** Synchronous read for render paths — accurate once the queue is hydrated.
 *  Counts only the SIGNED-IN account's writes: another user's parked writes
 *  can never drain for this session, so showing them would leave a permanent
 *  "N waiting to sync" the user cannot clear (iter358). */
export function pendingWritesSync(): number {
  if (!queue) return 0;
  const me = currentUserId();
  return queue.filter(e => !e.userId || e.userId === me).length;
}

export async function pendingWrites(): Promise<number> {
  await load();
  return pendingWritesSync();
}

function skipped(url: string): boolean {
  return SKIP.some(re => re.test(url));
}

export async function enqueueWrite(
  url: string,
  method: string,
  body: string | undefined,
  opts: { label?: string; dedupeKey?: string } = {},
): Promise<boolean> {
  if (skipped(url)) return false;
  if (body && body.length > MAX_BODY_BYTES) return false;
  const q = await load();
  const entry: QueuedWrite = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    url, method: method.toUpperCase(), body,
    label: opts.label || url.replace('/api/', ''),
    ts: Date.now(),
    attempts: 0,
    userId: currentUserId() || undefined,
    dedupeKey: opts.dedupeKey,
  };
  if (entry.dedupeKey) {
    const idx = q.findIndex(e => e.dedupeKey === entry.dedupeKey && e.userId === entry.userId);
    if (idx >= 0) {
      // Replace IN PLACE: appending at the end would reorder this write
      // relative to writes of other resources queued in between.
      entry.ts = q[idx].ts;
      q[idx] = entry;
      await persist();
      return true;
    }
  }
  q.push(entry);
  if (q.length > MAX_ENTRIES) q.splice(0, q.length - MAX_ENTRIES);
  await persist();
  return true;
}

/** A replayed write can carry server state the client needs. Today only the
 *  weight-goal stamp matters: without it the goal written at REPLAY time looks
 *  newer than the user's later pick and the sync would undo that pick.
 *  (Key duplicated from weightGoal.ts to avoid an import cycle.) */
async function adoptServerEcho(entry: QueuedWrite, res: Response): Promise<void> {
  if (!/user-bodyweight/.test(entry.url)) return;
  try {
    const data = await res.clone().json();
    const stamp = Date.parse(String(data?.weightGoalUpdatedAt || ''));
    if (stamp) localStorage.setItem('gym_notebook_weight_goal_updated_at', String(stamp));
  } catch { /* no body */ }
}

/** Replay the queue oldest-first. Stops at the first network failure so the
 *  original order is preserved. Returns the number still waiting. */
export async function flushQueue(): Promise<number> {
  if (flushing) return pendingWritesSync();
  const q = await load();
  if (!q.length) return 0;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return pendingWritesSync();
  const me = currentUserId();
  // Logged out: the token is gone, so every replay would 401. The writes stay
  // parked until their owner signs back in.
  if (!me) return pendingWritesSync();
  flushing = true;
  try {
    let i = 0;
    while (i < q.length) {
      const entry = q[i];
      if (Date.now() - entry.ts > MAX_AGE_MS) {
        // Only AGE retires an entry. Counting auth or connection failures
        // towards a cap would quietly bin a week of offline workouts after a
        // handful of retry cycles (iter356).
        q.splice(i, 1);
        continue;
      }
      if (entry.userId && entry.userId !== me) {
        // Belongs to another account on this device — skip, never replay it
        // into the account that happens to be signed in now (iter357).
        i += 1;
        continue;
      }
      const token = getToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      try {
        const res = await fetch(entry.url, {
          method: entry.method, headers, body: entry.body,
          credentials: 'include' as RequestCredentials,
        });
        if (res.ok) {
          await adoptServerEcho(entry, res);
          q.splice(i, 1);
          await persist();
          continue;
        }
        // 401/403 (session expired while offline), 408 and 429 are TEMPORARY —
        // dropping them here would silently bin every workout, meal and walk
        // logged during a long offline stretch. Stop and retry later instead.
        if (res.status === 401 || res.status === 403 || res.status === 408 || res.status === 429) {
          entry.attempts += 1;
          await persist();
          break;
        }
        if (res.status >= 400 && res.status < 500) {
          // The payload itself is rejected — it will never be accepted, so drop
          // it rather than blocking every later write behind it.
          q.splice(i, 1);
          await persist();
          continue;
        }
        entry.attempts += 1;
        await persist();
        break; // server error — try again on the next flush
      } catch {
        entry.attempts += 1;
        await persist();
        break; // still offline
      }
    }
    await persist();
    return pendingWritesSync();
  } finally {
    flushing = false;
  }
}

/** Send a mutating request, parking it for later if the network is down.
 *  Returns the Response, or null when the write was queued. */
export async function sendOrQueue(
  url: string,
  init: RequestInit & { method: string },
  opts: { label?: string; dedupeKey?: string } = {},
): Promise<Response | null> {
  const body = typeof init.body === 'string' ? init.body : undefined;
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (offline) {
    const queued = await enqueueWrite(url, init.method, body, opts);
    // Requests that must NOT be replayed later (login, AI, backup) still have
    // to fail loudly — returning null would read as "saved" to the caller.
    if (!queued) throw new Error('offline');
    return null;
  }
  try {
    const res = await fetch(url, init);
    if (res.status >= 500) {
      const queued = await enqueueWrite(url, init.method, body, opts);
      if (queued) return null;
    }
    return res;
  } catch (err) {
    const queued = await enqueueWrite(url, init.method, body, opts);
    if (!queued) throw err;
    return null;
  }
}

/** Flush on reconnect + every 60s while anything is waiting. Called once from
 *  the SyncProvider. */
export function startOfflineQueueWorker(): () => void {
  const onOnline = () => { void flushQueue(); };
  window.addEventListener('online', onOnline);
  const timer = setInterval(() => {
    if (pendingWritesSync() > 0) void flushQueue();
  }, 60_000);
  void load().then(() => { notify(); void flushQueue(); });
  return () => {
    window.removeEventListener('online', onOnline);
    clearInterval(timer);
  };
}
